import React, { useState, useMemo } from 'react';
import { v4 as uuid } from 'uuid';

import { Select } from 'shared/components';
import { CategoryOptions, SubcategoryOptions } from 'shared/constants';
import { GqlCustomNavigationItem, CustomNavigationItemType } from 'types/graphql';

import { ModalControllerProps } from './modal-controller';
import { BaseCustomNavigationModal } from './base-custom-navigation-modal';

type CategoryOption = {
  key: string;
  label: string;
  value: string | null;
  subcategories: Array<{ key: string; label: string; value: string }>;
};

type SubcategoryOption = {
  key: string;
  label: string;
  value: string;
};

export function AddCategoryModal({ editingItem, onClose, onSave }: ModalControllerProps): JSX.Element {
  const [selectedCategory, setSelectedCategory] = useState<CategoryOption | null>(() => {
    if (editingItem?.connectedId) {
      const categoryValue = editingItem.connectedId.split('/')[0];
      return CategoryOptions.find((cat) => cat.value === categoryValue) ?? null;
    }
    return null;
  });

  const [selectedSubcategory, setSelectedSubcategory] = useState<SubcategoryOption | null>(() => {
    if (editingItem?.connectedId?.includes('/')) {
      const subcategoryValue = editingItem.connectedId.split('/')[1];
      const categoryValue = editingItem.connectedId.split('/')[0];
      if (categoryValue && subcategoryValue) {
        const subcategories = SubcategoryOptions[categoryValue] ?? [];
        return subcategories.find((sub: SubcategoryOption) => sub.value === subcategoryValue) ?? null;
      }
    }
    return null;
  });

  const [label, setLabel] = useState(editingItem?.label ?? '');

  const categoryOptions = useMemo(
    () => CategoryOptions.filter((cat) => cat.value !== null && cat.value !== 'N/A' && cat.value !== ''),
    []
  );

  const subcategoryOptions: SubcategoryOption[] = useMemo(() => {
    if (!selectedCategory?.value) {
      return [];
    }
    return SubcategoryOptions[selectedCategory.value] ?? [];
  }, [selectedCategory]);

  const isEditing = !!editingItem;
  const isValid = selectedCategory !== null && !!selectedCategory.value && label.trim() !== '';

  const handleCategoryChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const category = categoryOptions.find((cat) => cat.key === e.target.value) ?? null;
    setSelectedCategory(category);
    setSelectedSubcategory(null);

    if (category) {
      setLabel(category.label);
    }
  };

  const handleSubcategoryChange = (e: React.ChangeEvent<HTMLSelectElement>): void => {
    const subcategory = subcategoryOptions.find((sub) => sub.key === e.target.value) ?? null;
    if (!subcategory) {
      if (selectedCategory) {
        setLabel(selectedCategory.label);
      }
      setSelectedSubcategory(null);

      return;
    }

    setSelectedSubcategory(subcategory);

    if (!editingItem) {
      setLabel(subcategory.label);
    }
  };

  const handleSave = (): void => {
    if (!isValid || !selectedCategory.value) {
      return;
    }

    const connectedId = selectedSubcategory?.value
      ? `${selectedCategory.value}/${selectedSubcategory.value}`
      : selectedCategory.value;

    const item: GqlCustomNavigationItem = {
      connectedId,
      id: editingItem?.id ?? uuid(),
      items: editingItem?.items ?? [],
      label: label.trim(),
      navigationType: CustomNavigationItemType.category,
      position: editingItem?.position ?? 0,
    };

    onSave(item);
    onClose();
  };

  return (
    <BaseCustomNavigationModal
      copy='Connect a product category.'
      handleSave={handleSave}
      headerText='Category'
      inputLabel={label}
      isEditing={isEditing}
      isOpen
      isValid={isValid}
      onClose={onClose}
      setLabel={setLabel}
    >
      <Select
        disabled={isEditing}
        fontSize={13}
        onChange={handleCategoryChange}
        value={selectedCategory?.key ?? ''}
        width='100%'
        mb='12px'
      >
        <option value='' disabled>
          Select a category
        </option>
        {categoryOptions.map((category) => (
          <option key={category.key} value={category.key}>
            {category.label}
          </option>
        ))}
      </Select>

      {subcategoryOptions.length > 0 && (
        <Select
          disabled={!selectedCategory || isEditing}
          fontSize={13}
          onChange={handleSubcategoryChange}
          value={selectedSubcategory?.key ?? ''}
          width='100%'
        >
          <option value=''>Select a subcategory (optional)</option>
          {subcategoryOptions.map((subcategory) => (
            <option key={subcategory.key} value={subcategory.key}>
              {subcategory.label}
            </option>
          ))}
        </Select>
      )}
    </BaseCustomNavigationModal>
  );
}
